import { cn } from 'cn';
import { type ReactElement } from 'react';

import { type UUID } from '~/types/common';

import { Spinner } from '@vestigas/ui/molecules';
import { Chip } from '~/components/baseComponents/chips/Chip';
import { ChipList } from '~/components/baseComponents/chips/ChipList';

type Item = {
  [key: string]: any;
  id: UUID;
};

type P<T extends Item> = {
  readonly currentItems: T[];
  readonly currentItemsFiltered: T[];
  readonly fetchNextPage?: () => void;
  readonly hasNextPage?: boolean;
  readonly isFetchingNextPage?: boolean;
  readonly label: string;
  readonly onSelectedItemClick: (id: UUID) => void;
  readonly renderItem?: (item: Item) => ReactElement | undefined;
} & ComponentStyling;

export const SelectedItemsDisplayMode = <T extends Item>({
  className,
  currentItems,
  currentItemsFiltered,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  label,
  onSelectedItemClick,
  renderItem,
  style,
}: P<T>) => (
  <div
    className={cn(
      'overflow-y-auto',
      {
        flex: currentItems.length === 0,
      },
      className,
    )}
    style={style}
  >
    {currentItemsFiltered.length > 0 && (
      <ChipList
        clickable={Boolean(onSelectedItemClick)}
        items={currentItemsFiltered.map((item) => ({
          ...item,
          className: 'bg-gray-200',
          sx: {},
        }))}
        renderItem={renderItem}
        onClick={onSelectedItemClick}
      />
    )}
    {currentItems.length === 0 && (
      <div className="m-auto my-auto p-2 text-center text-gray-500">
        <div className="whitespace-nowrap">
          Es sind noch keine {label} zugeordnet
        </div>
      </div>
    )}
    {hasNextPage ? (
      <div className="mt-1 flex flex-wrap items-center gap-1">
        <Chip
          key="pagination-key"
          className={cn(
            'border border-gray-200 transition-all duration-150 hover:border-transparent',
          )}
          name={
            isFetchingNextPage ? (
              <Spinner className="-translate-x-1.5" title="Lade Daten..." />
            ) : (
              '+ weitere Ergebnisse laden'
            )
          }
          isPaginationChip
          onPaginationClick={fetchNextPage}
        />
      </div>
    ) : null}
  </div>
);
